import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Upload, ArrowLeft, FileSpreadsheet, Check, AlertCircle, 
  X, Download, ChevronRight, Users
} from 'lucide-react';
import { useCommunity } from '../contexts/CommunityContext';

export default function ResidentImportPage() {
  const navigate = useNavigate();
  const { communityId } = useParams();
  const { importResidents, community } = useCommunity();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'importing' | 'done'
  const [parsedRows, setParsedRows] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);

  // ============================================
  // CSV Parsing
  // ============================================

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have a header row and at least one data row');
    }

    // Parse header
    const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());

    // Map common header variations to our field names
    const fieldMap = {};
    headers.forEach((header, idx) => {
      const h = header.replace(/[_\s.\-]+/g, '').toLowerCase();
      if (['firstname', 'first', 'fname', 'namefirst'].includes(h)) fieldMap.firstName = idx;
      else if (['lastname', 'last', 'lname', 'surname', 'namelast'].includes(h)) fieldMap.lastName = idx;
      else if (['preferredname', 'preferred', 'nickname', 'goby', 'goesby'].includes(h)) fieldMap.preferredName = idx;
      else if (['room', 'roomnumber', 'roomno', 'roomnum', 'unit'].includes(h)) fieldMap.roomNumber = idx;
      else if (['movein', 'moveindate', 'admitdate', 'admissiondate', 'dateadmitted'].includes(h)) fieldMap.moveInDate = idx;
      else if (['residentid', 'id', 'quilttid', 'quilttresidentid', 'externalid', 'rid'].includes(h)) fieldMap.quilttResidentId = idx;
      else if (['carelevel', 'care', 'levelofcare', 'loc', 'caretype', 'level'].includes(h)) fieldMap.careLevel = idx;
    });

    if (fieldMap.firstName === undefined || fieldMap.lastName === undefined) {
      // Try to detect "Name" or "Full Name" column and split
      const nameIdx = headers.findIndex(h => {
        const clean = h.replace(/[_\s-]+/g, '');
        return ['name', 'fullname', 'residentname', 'resident'].includes(clean);
      });

      if (nameIdx >= 0) {
        // Parse rows with full name splitting
        return lines.slice(1).map((line, i) => {
          const values = parseCSVLine(line);
          const fullName = values[nameIdx]?.trim() || '';
          const nameParts = fullName.split(/\s+/);
          
          return {
            rowNumber: i + 2,
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            preferredName: fieldMap.preferredName !== undefined ? values[fieldMap.preferredName]?.trim() || '' : '',
            roomNumber: fieldMap.roomNumber !== undefined ? values[fieldMap.roomNumber]?.trim() || '' : '',
            moveInDate: fieldMap.moveInDate !== undefined ? values[fieldMap.moveInDate]?.trim() || '' : '',
            quilttResidentId: fieldMap.quilttResidentId !== undefined ? values[fieldMap.quilttResidentId]?.trim() || '' : '',
            careLevel: fieldMap.careLevel !== undefined ? values[fieldMap.careLevel]?.trim() || '' : '',
            raw: line
          };
        });
      }

      throw new Error('CSV must contain "First Name" and "Last Name" columns (or a "Name" column). Found headers: ' + headers.join(', '));
    }

    // Parse rows
    return lines.slice(1).map((line, i) => {
      const values = parseCSVLine(line);
      return {
        rowNumber: i + 2,
        firstName: values[fieldMap.firstName]?.trim() || '',
        lastName: values[fieldMap.lastName]?.trim() || '',
        preferredName: fieldMap.preferredName !== undefined ? values[fieldMap.preferredName]?.trim() || '' : '',
        roomNumber: fieldMap.roomNumber !== undefined ? values[fieldMap.roomNumber]?.trim() || '' : '',
        moveInDate: fieldMap.moveInDate !== undefined ? values[fieldMap.moveInDate]?.trim() || '' : '',
        quilttResidentId: fieldMap.quilttResidentId !== undefined ? values[fieldMap.quilttResidentId]?.trim() || '' : '',
        careLevel: fieldMap.careLevel !== undefined ? values[fieldMap.careLevel]?.trim() || '' : '',
        raw: line
      };
    });
  };

  // Handle quoted CSV fields properly
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const rows = parseCSV(event.target.result);
        setParsedRows(rows);

        // Validate rows
        const valid = [];
        const invalid = [];

        rows.forEach(row => {
          if (!row.firstName || !row.lastName) {
            invalid.push({ ...row, error: 'Missing first or last name' });
          } else {
            valid.push(row);
          }
        });

        setValidRows(valid);
        setInvalidRows(invalid);
        setStep('preview');
      } catch (err) {
        setError(err.message);
      }
    };

    reader.readAsText(file);
  };

  const handleImport = async () => {
    setStep('importing');
    setError(null);

    try {
      const results = await importResidents(
        validRows.map(row => ({
          firstName: row.firstName,
          lastName: row.lastName,
          preferredName: row.preferredName || null,
          roomNumber: row.roomNumber || null,
          moveInDate: row.moveInDate || null,
          quilttResidentId: row.quilttResidentId || null,
          careLevel: row.careLevel || null
        }))
      );

      setImportResults(results);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Import failed. Please try again.');
      setStep('preview');
    }
  };

  const downloadTemplate = () => {
    const csv = 'Resident ID,First Name,Last Name,Preferred Name,Room Number\nQR-001,John,Smith,Johnny,101\nQR-002,Mary,Johnson,Mary,102\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resident-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/community/${communityId}`)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Import Residents</h1>
              <p className="text-sm text-gray-500">{community?.name}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Upload Step */}
        {step === 'upload' && (
          <div>
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <div 
                className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-quiltt-purple hover:bg-quiltt-50/30 transition-all"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload your resident roster</h3>
                <p className="text-gray-500 mb-4">Drop a CSV file here, or click to browse</p>
                <p className="text-sm text-gray-400">Supports CSV files with columns for name, room number, etc.</p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">CSV Format Guide</h3>
              <p className="text-sm text-gray-600 mb-4">
                Your CSV should include columns for resident names. Resident ID, room numbers, preferred names, and care level are optional. 
                If your list was exported from Quiltt, the Resident ID column will be matched automatically.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 font-mono text-sm text-gray-700 overflow-x-auto">
                Resident ID, First Name, Last Name, Preferred Name, Room Number<br />
                QR-001, John, Smith, Johnny, 101<br />
                QR-002, Mary, Johnson, Mary, 102<br />
                QR-003, Robert, Williams, Bob, 103
              </div>

              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 text-sm text-quiltt-purple hover:underline"
              >
                <Download className="w-4 h-4" />
                Download CSV template
              </button>
            </div>
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && (
          <div>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Preview: {fileName}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {validRows.length} resident{validRows.length !== 1 ? 's' : ''} ready to import
                    {invalidRows.length > 0 && `, ${invalidRows.length} row${invalidRows.length !== 1 ? 's' : ''} with issues`}
                  </p>
                </div>
                <button
                  onClick={() => { setStep('upload'); setParsedRows([]); setValidRows([]); setInvalidRows([]); }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Choose different file
                </button>
              </div>

              {/* Valid rows */}
              {validRows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-2 font-medium text-gray-600">Resident ID</th>
                        <th className="px-4 py-2 font-medium text-gray-600">First Name</th>
                        <th className="px-4 py-2 font-medium text-gray-600">Last Name</th>
                        <th className="px-4 py-2 font-medium text-gray-600">Preferred</th>
                        <th className="px-4 py-2 font-medium text-gray-600">Room</th>
                        <th className="px-4 py-2 font-medium text-gray-600">Care Level</th>
                        <th className="px-4 py-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {validRows.slice(0, 50).map((row, i) => (
                        <tr key={i} className="border-t border-gray-50">
                          <td className="px-4 py-2 text-gray-500 font-mono text-xs">{row.quilttResidentId || '—'}</td>
                          <td className="px-4 py-2 text-gray-900">{row.firstName}</td>
                          <td className="px-4 py-2 text-gray-900">{row.lastName}</td>
                          <td className="px-4 py-2 text-gray-500">{row.preferredName || '—'}</td>
                          <td className="px-4 py-2 text-gray-500">{row.roomNumber || '—'}</td>
                          <td className="px-4 py-2 text-gray-500">{row.careLevel || '—'}</td>
                          <td className="px-4 py-2">
                            <Check className="w-4 h-4 text-green-500" />
                          </td>
                        </tr>
                      ))}
                      {validRows.length > 50 && (
                        <tr className="border-t border-gray-50">
                          <td colSpan={6} className="px-4 py-2 text-center text-gray-500 text-sm">
                            ...and {validRows.length - 50} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Invalid rows */}
              {invalidRows.length > 0 && (
                <div className="border-t border-gray-200">
                  <div className="px-4 py-3 bg-amber-50 text-sm text-amber-800 font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {invalidRows.length} row{invalidRows.length !== 1 ? 's' : ''} will be skipped:
                  </div>
                  {invalidRows.map((row, i) => (
                    <div key={i} className="px-4 py-2 border-t border-amber-100 bg-amber-50/50 text-sm flex items-center justify-between">
                      <span className="text-gray-600">
                        Row {row.rowNumber}: "{row.firstName} {row.lastName}".trim() || 'Empty'
                      </span>
                      <span className="text-amber-700 text-xs">{row.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setStep('upload'); setParsedRows([]); }}
                className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={validRows.length === 0}
                className="flex-1 flex items-center justify-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                <Users className="w-4 h-4" />
                Import {validRows.length} Resident{validRows.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}

        {/* Importing Step */}
        {step === 'importing' && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="w-12 h-12 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Importing residents...</h3>
            <p className="text-gray-500">
              Creating life stories for {validRows.length} resident{validRows.length !== 1 ? 's' : ''}. This may take a moment.
            </p>
          </div>
        )}

        {/* Done Step */}
        {step === 'done' && importResults && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Import Complete!</h3>
            <p className="text-gray-600 mb-6">
              Successfully imported {importResults.imported} resident{importResults.imported !== 1 ? 's' : ''}.
              {importResults.skipped > 0 && ` ${importResults.skipped} row${importResults.skipped !== 1 ? 's' : ''} skipped.`}
            </p>

            {importResults.errors?.length > 0 && (
              <div className="mb-6 text-left bg-amber-50 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800 mb-2">Skipped rows:</p>
                {importResults.errors.map((err, i) => (
                  <p key={i} className="text-sm text-amber-700">
                    Row {err.row} ({err.name}): {err.error}
                  </p>
                ))}
              </div>
            )}

            <button
              onClick={() => navigate(`/community/${communityId}`)}
              className="inline-flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              View Resident Dashboard
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
